import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { SurveyStatus } from '@engage/types';
import { useSurveys, type Survey } from '../../api/surveys.api';
import { NewSurveyModal } from './NewSurveyModal';
import { CopyLinkButton } from '../../features/surveys/CopyLinkButton';
import { Btn, Icon, ICONS, StatusChip } from '../../features/forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../features/forms/ui/tokens';

const STATUS_OPTIONS: { value: SurveyStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Status · All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

export function SurveysPage() {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<SurveyStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);

  const { data, isLoading } = useSurveys();

  const rows = useMemo(() => {
    const base = data ?? [];
    const q = search.trim().toLowerCase();
    return base.filter((s) => {
      if (filterStatus !== 'ALL' && s.status !== filterStatus) return false;
      if (q && !s.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, filterStatus, search]);

  const counts = useMemo(() => {
    const base = data ?? [];
    return {
      total: base.length,
      published: base.filter((s) => s.status === 'PUBLISHED').length,
      drafts: base.filter((s) => s.status === 'DRAFT').length,
    };
  }, [data]);

  return (
    <div style={{ padding: '26px 30px', fontFamily: FORM_UI_FONT, color: C.ink }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 12, color: C.sub, fontWeight: 500 }}>Engage › Surveys</div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.indigo, margin: '4px 0 4px' }}>Surveys</h1>
          <div style={{ fontSize: 12.5, color: C.sub }}>
            {counts.total} surveys · {counts.published} published · {counts.drafts} drafts · multi-page,
            branching, with aggregated results
          </div>
        </div>
        <Btn kind="primary" onClick={() => setNewOpen(true)}>
          <Icon d={ICONS.plus} size={15} color="#fff" />
          Create survey
        </Btn>
      </div>

      <div style={{ display: 'flex', gap: 10, margin: '20px 0 14px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '0 0 300px' }}>
          <span style={{ position: 'absolute', left: 11, top: 9 }}>
            <Icon d={ICONS.search} size={16} color={C.faint} />
          </span>
          <input
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 12px 9px 34px',
              border: `1px solid ${C.line}`,
              borderRadius: 9,
              fontFamily: FORM_UI_FONT,
              fontSize: 13,
              background: '#fff',
              color: C.ink,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ position: 'relative' }}>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as SurveyStatus | 'ALL')}
            style={{
              appearance: 'none',
              padding: '9px 32px 9px 13px',
              border: `1px solid ${C.line}`,
              borderRadius: 9,
              fontSize: 13,
              fontFamily: FORM_UI_FONT,
              fontWeight: 600,
              color: C.indigoSoft,
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span style={{ position: 'absolute', right: 11, top: 11, pointerEvents: 'none' }}>
            <Icon d={ICONS.chev} size={13} color={C.faint} sw={2} />
          </span>
        </div>
      </div>

      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: C.sub }}>Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState onCreate={() => setNewOpen(true)} />
      ) : (
        <SurveysTable rows={rows} onRowClick={(id) => navigate(`/surveys/${id}`)} />
      )}

      {newOpen && (
        <NewSurveyModal
          onClose={() => setNewOpen(false)}
          onCreated={(survey) => {
            setNewOpen(false);
            navigate(`/surveys/${survey.id}`);
          }}
        />
      )}
    </div>
  );
}

function SurveysTable({ rows, onRowClick }: { rows: Survey[]; onRowClick: (id: string) => void }) {
  const navigate = useNavigate();
  const GRID = '1fr 120px 120px 120px 90px';
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.line}`, overflow: 'hidden' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: GRID,
          padding: '13px 20px',
          borderBottom: `1px solid ${C.line}`,
          fontSize: 11.5,
          fontWeight: 700,
          color: C.faint,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}
      >
        <div>Survey</div>
        <div>Responses</div>
        <div>Status</div>
        <div>Last updated</div>
        <div />
      </div>
      {rows.map((s, i) => {
        const count = s.response_count ?? 0;
        return (
          <div
            key={s.id}
            onClick={() => onRowClick(s.id)}
            style={{
              display: 'grid',
              gridTemplateColumns: GRID,
              padding: '15px 20px',
              alignItems: 'center',
              borderBottom: i < rows.length - 1 ? `1px solid ${C.line}` : 'none',
              cursor: 'pointer',
            }}
          >
            <div>
              <Link to={`/surveys/${s.id}`} style={{ fontSize: 13.5, fontWeight: 600, color: C.ink, textDecoration: 'none' }}>
                {s.name}
              </Link>
              <div style={{ fontSize: 11.5, color: C.faint, marginTop: 2 }}>{s.description ?? '-'}</div>
            </div>
            <div>
              {count > 0 ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/surveys/${s.id}/results`);
                  }}
                  title="View results"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 999,
                    border: `1px solid ${C.teal}`,
                    background: '#fff',
                    color: C.tealDark,
                    fontFamily: FORM_UI_FONT,
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {count.toLocaleString()}
                  <Icon d={ICONS.chev} size={12} color={C.tealDark} sw={2} />
                </button>
              ) : (
                <span style={{ fontSize: 12.5, color: C.faint }}>0</span>
              )}
            </div>
            <div>
              <StatusChip status={s.status} />
            </div>
            <div style={{ fontSize: 12.5, color: C.sub }}>{relativeTime(s.updated_at)}</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, color: C.faint }}>
              {s.status === 'PUBLISHED' && s.audience_mode === 'ANONYMOUS' && (
                <CopyLinkButton slug={s.slug} variant="icon" />
              )}
              <Icon d={ICONS.dots} size={18} color={C.faint} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.line}`, padding: '40px 28px', textAlign: 'center' }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: C.candidateBg,
          color: C.indigo,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 14,
        }}
      >
        <Icon d={ICONS.layers} size={22} color={C.indigo} />
      </div>
      <h2 style={{ fontSize: 18, fontWeight: 800, color: C.indigo, margin: '0 0 6px' }}>No surveys yet</h2>
      <p style={{ fontSize: 13, color: C.sub, margin: '0 0 18px', maxWidth: 480, marginInline: 'auto' }}>
        Surveys are multi-page questionnaires with branching, rating scales (numeric, Likert, NPS) and
        aggregated results. Run them as open public links or send tokenised links to known contacts.
      </p>
      <Btn kind="primary" onClick={onCreate}>
        <Icon d={ICONS.plus} size={15} color="#fff" /> Create your first survey
      </Btn>
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}
